import './types';

export const LEVELS = {
    off: 0,
    error: 1,
    warn: 2,
    info: 3,
    debug: 4,
};

/**
 * @callback LazyMessageCallback
 * The logged message could also be a function which works like a syncronous
 * message getter that is called only after successful shouldLogMessage check.
 *
 * The common use case for lazy messages is data that
 *  - require some pre-calculations
 *  - and are logged at info or debug level.
 *
 * Since these levels are disabled most of the time, we need to avoid the impact
 * of such calculations on the app performance. Don't worry about function creation -
 * it is far more effective than copying even a small, plain object or converting it to JSON.
 *
 * @returns {any[]} array of message parts
 */

/** @typedef {((...messageParts: any[]) => void) | ((f: LazyMessageCallback) => void)} LevelLogMethod */

export class PrefixedLogger {
    snapshotProvider = null;

    constructor({ log, prefix }) {
        this.baseLog = log;
        this.prefix = prefix;
    }

    log(lvl, ...messageParts) {
        return this.baseLog(lvl, this.prefix, ...messageParts);
    }

    /**
     * @type LevelLogMethod
     * @example
     * logger.error('Failed to create a meeting', err);
     */
    error(...messageParts) {
        return this.log('error', ...messageParts);
    }

    /** @type LevelLogMethod */
    warn(...messageParts) {
        return this.log('warn', ...messageParts);
    }

    /**
     * @type LevelLogMethod
     * @example
     * logger.info('calendar events:', events.today, events.tomorrow, ...);
     * @example
     * // can be used with lazy evaluation too:
     * logger.info(() => ['calendar events:', JSON.stringify(events)]);
     */
    info(...messageParts) {
        return this.log('info', ...messageParts);
    }

    /** @type LevelLogMethod */
    debug(...messageParts) {
        return this.log('debug', ...messageParts);
    }

    registerSnapshotProvider(snapshotProvider) {
        if (typeof snapshotProvider === 'function') {
            this.snapshotProvider = snapshotProvider;
        } else {
            this.baseLog(
                'error',
                'prefixed-logger',
                'registerSnapshotProvider should be called with a function. calling logger prefix:' +
                    this.prefix
            );
        }
    }

    async getSnapshot() {
        if (typeof this.snapshotProvider === 'function') {
            try {
                const snapshot = await this.snapshotProvider();
                // ensure the snapshot is json serialization-safe
                JSON.stringify(snapshot);
                return snapshot;
            } catch (e) {
                this.baseLog(
                    'warn',
                    'prefixed-logger',
                    `call snapshotProvider for prefix ${this.prefix} failed`,
                    e
                );
                return null;
            }
        }

        return null;
    }
}

export class LoggerFactory {
    /**
     * @param {Object} options - options to configure log factory
     * @param {string} [options.prefixes] - pattern to apply max priority for messages with matching prefix
     * @param {LoggerRuntimeOpts} [options.opts] - options to configure logger factory
     */
    loggers = new Map();

    constructor({
        level = LoggerFactory.defaults.level,
        transportLevels = {},
        transports = {},
        prefixes = '',
        format = LoggerFactory.defaults.format,
    }) {
        this.levels = LEVELS;
        this.cacheLoggerPrefix = new Map();
        this.applyRuntimeOpts({
            level,
            transportLevels,
            transports,
            prefixes,
            format,
        });
    }

    lte = (l1, l2) => this.levels[l1] <= this.levels[l2];

    /** Prevents using `off` level for logging messages */
    isLogLevelValid(level) {
        return Number.isInteger(LEVELS[level]) && LEVELS[level] > 0;
    }

    /**
     * For each transport checks whether the message should be logged
     * and, if so, formats it and passes to the .log() method of the transport.
     * @param {string} lvl
     * @param {string} prefix
     * @param {...any} messageParts
     * @returns {Promise.<[any], Error>} Promise containing an array of all transport log results
     */
    log = (lvl, prefix, ...messageParts) => {
        if (!this.isLogLevelValid(lvl, this.getLevels())) {
            throw new Error(`Invalid log level: ${lvl}`);
        }

        const { transports, format } = this.getRuntimeOpts();
        if (messageParts.length === 0) return;

        let formattedMessage = null;

        Object.entries(transports).forEach(([name, transport]) => {
            if (!this.memoizeShouldLogMessage(lvl, prefix, name)) return;

            if (!formattedMessage) {
                const lazyMessageParts = messageParts[0];
                formattedMessage = format(
                    lvl,
                    prefix,
                    typeof lazyMessageParts === 'function' ? lazyMessageParts() : messageParts
                );
            }

            return transport.log(formattedMessage);
        });
    };

    /**
     * Creates prefix logger
     * @param {string} prefix - a string that can identify the module we want to cover with logs
     * @returns {PrefixedLogger} prefix logger instance
     */
    byPrefix = prefix => {
        let logger = this.loggers.get(prefix);

        if (!logger) {
            logger = new PrefixedLogger({ log: this.log, prefix });
            this.loggers.set(prefix, logger);
        }

        return logger;
    };

    prefixesToRegExpList(prefixes) {
        return (
            (prefixes &&
                prefixes.length > 0 &&
                prefixes
                    ?.split(/[, ]+/)
                    .map(prefix => new RegExp(`${prefix.replace('*', '.*?')}`))) ||
            []
        );
    }

    /**
     * Memoize checks whether a message should be passed to transport.log
     * @param {string} lvl
     * @param {string} prefix
     * @param {string} transportName
     * @returns {boolean}
     */
    memoizeShouldLogMessage(lvl, prefix, transportName) {
        const cacheLoggerPrefixKey = `${lvl}-${prefix}-${transportName}`;

        let cacheLoggerPrefixValue = this.cacheLoggerPrefix.get(cacheLoggerPrefixKey);

        if (cacheLoggerPrefixValue === undefined) {
            cacheLoggerPrefixValue = this.shouldLogMessage(lvl, prefix, transportName);

            this.cacheLoggerPrefix.set(cacheLoggerPrefixKey, cacheLoggerPrefixValue);
        }

        return cacheLoggerPrefixValue;
    }

    /**
     * Checks whether a message should be passed to transport.log
     * @param {string} lvl
     * @param {string} prefix
     * @param {string} transportName
     * @returns {boolean}
     */
    shouldLogMessage(lvl, prefix, transportName) {
        return (
            this.isLogAllowedByTransport(lvl, transportName) &&
            this.isLogAllowedByLogger(lvl, prefix)
        );
    }

    isLogAllowedByLogger(lvl, prefix) {
        const loggerLevel = this.getLevel();
        return (
            loggerLevel !== 'off' &&
            (this.lte(lvl, loggerLevel) ||
                this.getRuntimeOpts().prefixREs.some(prefixRE => prefix?.match(prefixRE)))
        );
    }

    isLogAllowedByTransport(lvl, transportName) {
        const transportLevel = this.getTransportLevel(transportName);
        return transportLevel === null || this.lte(lvl, transportLevel);
    }

    async getSnapshots() {
        const snapShotEntities = await Promise.all(
            Array.from(this.loggers).map(async ([prefix, logger]) => [
                prefix,
                await logger.getSnapshot(),
            ])
        );
        return Object.fromEntries(snapShotEntities.filter(([, snapshot]) => snapshot !== null));
    }

    // ***************************** SETTERS ***************************** //

    setPrefixes(prefixes) {
        if (typeof prefixes !== 'string') return;

        this.cacheLoggerPrefix.clear();

        const prefixREs = this.prefixesToRegExpList(prefixes);

        this.runtimeOpts.prefixes = prefixes;
        this.runtimeOpts.prefixREs = prefixREs;
    }

    setTransportLevels(levels) {
        if (typeof levels !== 'object') return;

        this.cacheLoggerPrefix.clear();
        this.runtimeOpts.transportLevels = {
            ...this.runtimeOpts.transportLevels,
            ...levels,
        };
    }

    /**
     * Merges passed options with the currently used
     * @param {LoggerRuntimeOpts} options - options to overwrite
     */
    applyRuntimeOpts({ prefixes, level, transportLevels, format, ...opts }) {
        this.cacheLoggerPrefix.clear();

        this.runtimeOpts = { ...this.runtimeOpts, ...opts };

        if (this.levels[level] !== undefined) {
            this.runtimeOpts.level = level;
        }

        this.setPrefixes(prefixes);
        this.setTransportLevels(transportLevels);
        this.setFormatFunc(format);
    }

    /**
     * Sets func to format message
     * @param {LoggerFormatFunc} format
     */
    setFormatFunc(format) {
        if (typeof format !== 'function') return;

        this.cacheLoggerPrefix.clear();

        this.runtimeOpts.format = format;
    }

    // ***************************** GETTERS ***************************** //

    /** @returns {string} currently used logger level */
    getLevel() {
        return this.runtimeOpts.level;
    }

    /** @returns {Object.<string, string>} levels map */
    getLevels() {
        return this.levels;
    }

    /** @returns {LoggerFormatFunc} */
    getFormatFunc() {
        return this.runtimeOpts.format;
    }

    /** @returns {Transports} transport instance by name */
    getTransports() {
        return this.getRuntimeOpts().transports;
    }

    /** @returns {AbstractTransport} transport instance by name */
    getTransport(name) {
        return this.getTransports()[name];
    }

    /** @returns {Object.<string, string>} levels by transport names */
    getTransportLevels() {
        return this.getRuntimeOpts().transportLevels;
    }

    /** @returns {string|null} transport level by its name */
    getTransportLevel(name) {
        return this.getTransportLevels()[name] || null;
    }

    /** @returns {LoggerRuntimeOpts} logger options that can be changed at runtime */
    getRuntimeOpts() {
        return this.runtimeOpts;
    }

    /** @returns {string} currently used prefixes pattern */
    getPrefixes() {
        return this.getRuntimeOpts().prefixes;
    }
}

let logSeq = 0;

LoggerFactory.defaults = {
    level: 'warn',
    /** @type {function(string, ...any): LoggerFormattedMessage} */
    format: (lvl, prefix, messageParts) => {
        if (typeof messageParts?.map === 'function') {
            messageParts = messageParts.map(part => {
                // Avoid logging DOM elements to avoid memory leaks
                if (part?.nodeType) {
                    return `Node: ${part.nodeName};${part.nodeType};${part.id}`;
                }

                if (part?.window) {
                    return `Window: ${part?.name}`;
                }

                return part;
            });
        }

        return {
            seq: logSeq++,
            lvl,
            prefix,
            tsz: new Date().toISOString(),
            parts: messageParts,
        };
    },
};
